import _ from 'lodash';
import { t } from '@lingui/macro';
import { makeStyles } from '@material-ui/core/styles';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import Button from '@material-ui/core/Button';
import { i18n } from '@lingui/core';
import AgentDownloadWizardModel from './AgentDownloadWizardModel';
import AgentDownloadWizardOS from './AgentDownloadWizardOS';
import AgentDownloadWizardInstall from './AgentDownloadWizardInstall';
import AgentDownloadWizardEmail from './AgentDownloadWizardEmail';
import { useSMTPEnabled } from '../../../stores/EnvironmentStore';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        '& .MuiStepper-root': {
            paddingTop: 0,
        },
    },
    button: {
        marginTop: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    actionsContainer: {
        marginBottom: theme.spacing(2),
    },
    resetContainer: {
        padding: theme.spacing(3),
    },
}));

const modelStepTitle = i18n._(t`Select a virtual nPoint model`);
const osStepTitle = i18n._(t`Select the host operating system`);
const downloadStepTitle = i18n._(t`nPoint installation instructions`);
const emailStepTitle = i18n._(t`Email nPoint installation instructions (optional)`);
const closeButtonText = i18n._(t`Close`);
const continueButtonText = i18n._(t`Next`);
const backButtonText = i18n._(t`Back`);

function getWizardSteps(wizardSelections, smtpEnabled) {
    const steps = [
        {
            stepTitle: modelStepTitle,
            hideBackButton: true,
            component: <AgentDownloadWizardModel wizardSelections={wizardSelections} />,
        },
        {
            stepTitle: osStepTitle,
            component: <AgentDownloadWizardOS wizardSelections={wizardSelections} />,
        },
        {
            stepTitle: downloadStepTitle,
            component: <AgentDownloadWizardInstall wizardSelections={wizardSelections} />,
        },
    ];

    if (smtpEnabled) {
        steps.push({
            stepTitle: emailStepTitle,
            component: <AgentDownloadWizardEmail wizardSelections={wizardSelections} />,
        });
    }

    return steps;
}

const instructionsStepIndex = 2;

export default function AgentDownloadWizard({
    closeDialog,
    wizardSelections,
    wizardDefaults,
    activeStep,
    setActiveStep,
}) {
    const classes = useStyles();
    const [smtpEnabled] = useSMTPEnabled();

    const handleNext = () => {
        if (activeStep === steps.length - 1) {
            // they are done...reset and close
            wizardSelections.current = wizardDefaults;
            return closeDialog();
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const steps = getWizardSteps(wizardSelections, smtpEnabled);

    return (
        <div className={classes.root}>
            <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((stepObject) => (
                    <Step key={_.uniqueId()}>
                        <StepLabel>{stepObject.stepTitle}</StepLabel>
                        <StepContent>
                            {stepObject.component}
                            <div className={classes.actionsContainer}>
                                <div>
                                    {activeStep === instructionsStepIndex && smtpEnabled && (
                                        <Button onClick={closeDialog} className={classes.button} size="small">
                                            {closeButtonText}
                                        </Button>
                                    )}
                                    <Button
                                        disabled={activeStep === 0}
                                        onClick={handleBack}
                                        className={classes.button}
                                        size="small"
                                    >
                                        {backButtonText}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleNext}
                                        className={classes.button}
                                        size="small"
                                    >
                                        {activeStep === steps.length - 1 ? closeButtonText : continueButtonText}
                                    </Button>
                                </div>
                            </div>
                        </StepContent>
                    </Step>
                ))}
            </Stepper>
        </div>
    );
}
